import {
  columnOf,
  createTaskSchema,
  taskPriorities,
  taskPriorityLabels,
  taskStatusLabels,
  taskTypeLabels,
  taskTypes,
  type Task,
  type TaskPriority,
  type TaskStatus,
  type TaskType,
} from '@nexo/types';
import {
  Avatar,
  Button,
  DatePicker,
  IconButton,
  Popover,
  Select,
  Sheet,
  cn,
  type SelectOption,
} from '@nexo/ui';
import {
  CalendarDays,
  Check,
  CircleDashed,
  Flag,
  Hash,
  Lock,
  MessageSquare,
  Trash2,
  Users,
  X,
  Repeat,
  Shapes,
  Gauge,
  IterationCw,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam, type TaskDraft } from '../../layouts/TeamContext';
import { dueState, formatDueDate, formatLongDate, isoDateFromToday } from '../../lib/format';
import { useColumns, useSprints, useTasks } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { PriorityIcon, StatusIcon, TaskKey, TypeIcon } from './TaskIcons';
import { RecurrencePicker, TaskChecklist } from './TaskChecklist';
import { useCreateTask, useDeleteTask, useUpdateTask } from './useTaskMutations';
import { can } from '../../lib/permissions';
import { SourceMessage } from './SourceMessage';
import { errorMessage } from '../../lib/forms';
import { columnName } from './taskUtils';

interface Values {
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  assigneeIds: string[];
  channelId: string | null;
  type: TaskType;
  storyPoints: number | null;
  sprintId: string | null;
  columnId: string | null;
}

/** Usual estimation scale (Fibonacci-like, as in planning poker). */
const POINTS = [0, 1, 2, 3, 5, 8, 13, 21];

/** Side panel to create or edit a task. Edits are saved as you go. */
export function TaskDrawer({ draft, onClose }: { draft: TaskDraft | null; onClose: () => void }) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const tasks = useTasks(team.id);
  const task =
    draft?.mode === 'edit' ? tasks.data?.find((item) => item.id === draft.taskId) : undefined;
  const open = draft !== null && (draft.mode === 'create' || task !== undefined);

  return (
    <Sheet open={open} onClose={onClose} label={task ? t('Detalle de la tarea') : t('Nueva tarea')}>
      {draft?.mode === 'create' && (
        <CreateTaskForm key={JSON.stringify(draft)} draft={draft} onClose={onClose} />
      )}
      {task && <EditTask key={task.id} task={task} onClose={onClose} />}
    </Sheet>
  );
}

function EditTask({ task, onClose }: { task: Task; onClose: () => void }) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const me = useMe();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const canDelete = task.createdBy.id === me.id || can(team, 'manage_tasks');
  const column = columnOf(task, useColumns(team.id).data ?? []);

  const save = (input: Parameters<typeof update.mutate>[0]['input']) =>
    update.mutate({ id: task.id, input });

  return (
    <div className="flex h-full flex-col">
      <DrawerHeader onClose={onClose}>
        <TypeIcon type={task.type} />
        <TaskKey task={task} className="text-[13px]" />
        <span className="text-subtle">·</span>
        <StatusIcon status={task.status} />
        <span className="text-[13px] font-medium text-muted">
          {column ? columnName(column.name) : t(taskStatusLabels[task.status])}
        </span>
        <span className="ml-auto" />
        {canDelete && (
          <IconButton
            label={t('Borrar tarea')}
            className="hover:text-danger"
            onClick={() => {
              if (window.confirm(t('¿Borrar esta tarea?')))
                remove.mutate(task.id, { onSuccess: onClose });
            }}
          >
            <Trash2 />
          </IconButton>
        )}
      </DrawerHeader>

      <div className="scrollbar-thin flex-1 overflow-y-auto px-6 py-5">
        <InlineText
          value={task.title}
          onSave={(title) => title.trim() && title !== task.title && save({ title: title.trim() })}
          className="text-2xl leading-tight font-semibold tracking-tight"
          placeholder={t('Título de la tarea')}
          label={t('Título')}
          maxLength={200}
        />
        <InlineText
          value={task.description ?? ''}
          onSave={(description) =>
            description !== (task.description ?? '') &&
            save({ description: description.trim() || null })
          }
          className="mt-2 min-h-16 text-sm leading-relaxed text-text"
          placeholder={t('Añade una descripción…')}
          label={t('Descripción')}
          multiline
          maxLength={5000}
        />

        <Properties
          values={{
            title: task.title,
            description: task.description ?? '',
            status: task.status,
            priority: task.priority,
            dueDate: task.dueDate,
            assigneeIds: task.assignees.map((a) => a.id),
            channelId: task.channelId,
            type: task.type,
            storyPoints: task.storyPoints,
            sprintId: task.sprintId,
            columnId: task.columnId,
          }}
          onChange={(patch) => {
            const {
              channelId: _channel,
              title: _title,
              description: _description,
              columnId,
              status: _status,
              ...rest
            } = patch;
            // The column carries the status.
            save(columnId ? { ...rest, columnId } : rest);
          }}
          channelEditable={false}
        />
        <dl className="grid grid-cols-[8.5rem_1fr] items-center gap-x-3 text-sm">
          <PropertyLabel icon={<Repeat />}>{t('Repetir')}</PropertyLabel>
          <dd>
            <RecurrencePicker task={task} />
          </dd>
        </dl>

        <TaskChecklist task={task} />

        {task.sourceMessageId && (
          <SourceMessage messageId={task.sourceMessageId} onNavigate={onClose} />
        )}
      </div>

      <footer className="flex items-center gap-2 border-t border-border px-6 py-3 text-xs text-subtle">
        <Avatar
          id={task.createdBy.id}
          name={task.createdBy.displayName}
          src={task.createdBy.avatarUrl}
          size="xs"
        />
        {t('Creada por {{name}} el {{date}}', {
          name: task.createdBy.displayName,
          date: formatLongDate(task.createdAt),
        })}
        {task.completedAt && (
          <span className="ml-auto text-success">
            {t('Completada el {{date}}', { date: formatLongDate(task.completedAt) })}
          </span>
        )}
      </footer>
    </div>
  );
}

function CreateTaskForm({
  draft,
  onClose,
}: {
  draft: Extract<TaskDraft, { mode: 'create' }>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateTask();
  const [values, setValues] = useState<Values>({
    title: draft.title ?? '',
    description: '',
    status: draft.status ?? 'todo',
    priority: 'normal',
    dueDate: null,
    assigneeIds: draft.assigneeIds ?? [],
    channelId: draft.channelId ?? null,
    type: 'task',
    storyPoints: null,
    sprintId: draft.sprintId ?? null,
    columnId: draft.columnId ?? null,
  });
  const [error, setError] = useState<string | null>(null);

  function submit() {
    const parsed = createTaskSchema.safeParse({
      ...values,
      description: values.description.trim() || undefined,
      sourceMessageId: draft.sourceMessageId ?? null,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('Datos no válidos'));
      return;
    }
    create.mutate(parsed.data, {
      onSuccess: onClose,
      onError: (err) => setError(errorMessage(err)),
    });
  }

  return (
    <form
      className="flex h-full flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <DrawerHeader onClose={onClose}>
        <span className="text-[13px] font-medium text-muted">
          {draft.sourceMessageId ? t('Convertir mensaje en tarea') : t('Nueva tarea')}
        </span>
      </DrawerHeader>
      <div className="scrollbar-thin flex-1 overflow-y-auto px-6 py-5">
        <AutoTextarea
          autoFocus
          value={values.title}
          onChange={(title) => {
            setValues((v) => ({ ...v, title }));
            setError(null);
          }}
          onEnter={submit}
          placeholder={t('¿Qué hay que hacer?')}
          aria-label={t('Título')}
          maxLength={200}
          className="text-2xl leading-tight font-semibold tracking-tight"
        />
        <AutoTextarea
          value={values.description}
          onChange={(description) => setValues((v) => ({ ...v, description }))}
          placeholder={t('Añade una descripción…')}
          aria-label={t('Descripción')}
          maxLength={5000}
          className="mt-2 min-h-16 text-sm leading-relaxed"
        />
        <Properties
          values={values}
          onChange={(patch) => setValues((v) => ({ ...v, ...patch }))}
          channelEditable={!draft.sourceMessageId}
        />
        {draft.sourceMessageId && <SourceMessage messageId={draft.sourceMessageId} />}
        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}
      </div>
      <footer className="flex justify-end gap-2 border-t border-border px-6 py-3">
        <Button variant="ghost" onClick={onClose}>
          {t('Cancelar')}
        </Button>
        <Button type="submit" loading={create.isPending}>
          {t('Crear tarea')}
        </Button>
      </footer>
    </form>
  );
}

function DrawerHeader({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4 sm:px-6">
      {children}
      {/* Initial focus goes to "close" so Enter/Space can never trigger a destructive action. */}
      <IconButton
        label={t('Cerrar')}
        autoFocus
        onClick={onClose}
        className={cn(!children && 'ml-auto')}
      >
        <X />
      </IconButton>
    </header>
  );
}

/** Label/control rows for the task properties. */
function Properties({
  values,
  onChange,
  channelEditable,
}: {
  values: Values;
  onChange: (patch: Partial<Values>) => void;
  channelEditable: boolean;
}) {
  const { t } = useTranslation();
  const { channels, team } = useTeam();
  const sprints = useSprints(team.id).data ?? [];
  const columns = useColumns(team.id).data ?? [];
  const column = columnOf(values, columns);
  const channel = values.channelId ? channels.find((c) => c.id === values.channelId) : undefined;
  const sprintOptions = sprints.filter((s) => s.status !== 'completed' || s.id === values.sprintId);
  const points =
    values.storyPoints === null || POINTS.includes(values.storyPoints)
      ? POINTS
      : [...POINTS, values.storyPoints].sort((a, b) => a - b);

  return (
    <dl className="mt-6 grid grid-cols-[8.5rem_1fr] items-center gap-x-3 gap-y-1 text-sm">
      <PropertyLabel icon={<Shapes />}>{t('Tipo')}</PropertyLabel>
      <dd>
        <GhostSelect
          label={t('Tipo')}
          value={values.type}
          onChange={(type) => onChange({ type: type as TaskType })}
          options={taskTypes.map((type) => ({
            value: type,
            label: t(taskTypeLabels[type], { context: 'taskType' }),
          }))}
          icon={<TypeIcon type={values.type} />}
        />
      </dd>

      <PropertyLabel icon={<CircleDashed />}>{t('Estado')}</PropertyLabel>
      <dd>
        <GhostSelect
          label={t('Estado')}
          value={column?.id ?? ''}
          onChange={(id) => {
            const next = columns.find((c) => c.id === id);
            if (next) onChange({ columnId: next.id, status: next.category });
          }}
          options={columns.map((c) => ({
            value: c.id,
            label: columnName(c.name),
            icon: <StatusIcon status={c.category} />,
          }))}
          icon={<StatusIcon status={values.status} />}
        />
      </dd>

      <PropertyLabel icon={<Flag />}>{t('Prioridad')}</PropertyLabel>
      <dd>
        <GhostSelect
          label={t('Prioridad')}
          value={values.priority}
          onChange={(priority) => onChange({ priority: priority as TaskPriority })}
          options={taskPriorities.map((p) => ({
            value: p,
            label: t(taskPriorityLabels[p], { context: 'priority' }),
          }))}
          icon={<PriorityIcon priority={values.priority} showNormal />}
        />
      </dd>

      <PropertyLabel icon={<Users />}>{t('Responsables')}</PropertyLabel>
      <dd>
        <AssigneePicker
          value={values.assigneeIds}
          onChange={(assigneeIds) => onChange({ assigneeIds })}
        />
      </dd>

      <PropertyLabel icon={<IterationCw />}>{t('Sprint')}</PropertyLabel>
      <dd>
        <GhostSelect
          label={t('Sprint')}
          value={values.sprintId ?? ''}
          onChange={(id) => onChange({ sprintId: id || null })}
          options={[
            { value: '', label: t('Backlog (sin sprint)') },
            ...sprintOptions.map((s) => ({
              value: s.id,
              label: s.status === 'active' ? t('{{name}} (activo)', { name: s.name }) : s.name,
            })),
          ]}
        />
      </dd>

      <PropertyLabel icon={<Gauge />}>{t('Estimación')}</PropertyLabel>
      <dd>
        <GhostSelect
          label={t('Puntos de historia')}
          value={values.storyPoints === null ? '' : String(values.storyPoints)}
          onChange={(v) => onChange({ storyPoints: v === '' ? null : Number(v) })}
          options={[
            { value: '', label: t('Sin estimar') },
            ...points.map((p) => ({
              value: String(p),
              label: p === 1 ? t('1 punto') : t('{{count}} puntos', { count: p }),
            })),
          ]}
        />
      </dd>

      <PropertyLabel icon={<CalendarDays />}>{t('Fecha límite')}</PropertyLabel>
      <dd>
        <DuePicker
          value={values.dueDate}
          done={values.status === 'done'}
          onChange={(dueDate) => onChange({ dueDate })}
        />
      </dd>

      <PropertyLabel icon={<Hash />}>{t('Conversación')}</PropertyLabel>
      <dd>
        {channelEditable ? (
          <GhostSelect
            label={t('Conversación')}
            value={values.channelId ?? ''}
            onChange={(id) => onChange({ channelId: id || null })}
            options={[
              { value: '', label: t('Ninguna (todo el equipo)') },
              ...channels.map((c) => ({
                value: c.id,
                label: c.kind === 'dm' ? `@${c.dmPeer?.displayName ?? ''}` : `#${c.name}`,
              })),
            ]}
          />
        ) : (
          <span className="inline-flex h-8 items-center gap-1.5 px-2 text-muted">
            {channel ? (
              <>
                {channel.kind === 'dm' ? (
                  <MessageSquare className="size-4" />
                ) : channel.isPrivate ? (
                  <Lock className="size-4" />
                ) : (
                  <Hash className="size-4" />
                )}
                {channel.kind === 'dm' ? channel.dmPeer?.displayName : channel.name}
              </>
            ) : (
              t('Todo el equipo')
            )}
          </span>
        )}
      </dd>
    </dl>
  );
}

function PropertyLabel({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <dt className="flex h-8 items-center gap-2 text-[13px] text-muted [&_svg]:size-4 [&_svg]:text-subtle">
      {icon}
      {children}
    </dt>
  );
}

function GhostSelect({
  label,
  value,
  onChange,
  options,
  icon,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  icon?: ReactNode;
}) {
  return (
    <Select
      aria-label={label}
      value={value}
      onChange={onChange}
      options={options}
      icon={icon}
      variant="ghost"
      size="sm"
    />
  );
}

function AssigneePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const { members } = useTeam();
  const me = useMe();
  const selected = members.filter((m) => value.includes(m.id));
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <Popover
      align="left"
      className="w-64 p-1.5"
      trigger={({ toggle: open }) => (
        <button
          type="button"
          onClick={open}
          className="flex min-h-8 flex-wrap items-center gap-1.5 rounded-lg px-2 py-1 text-left hover:bg-surface-2"
        >
          {selected.length === 0 ? (
            <span className="text-subtle">{t('Sin asignar')}</span>
          ) : (
            selected.map((m) => (
              <span
                key={m.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 py-0.5 pr-2 pl-0.5 ring-1 ring-border"
              >
                <Avatar id={m.id} name={m.displayName} src={m.avatarUrl} size="xs" />
                <span className="text-[13px] font-medium">
                  {m.id === me.id ? t('Yo') : m.displayName}
                </span>
              </span>
            ))
          )}
        </button>
      )}
    >
      {() => (
        <ul role="listbox" aria-label={t('Responsables')} aria-multiselectable>
          {members.map((member) => {
            const isSelected = value.includes(member.id);
            return (
              <li key={member.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggle(member.id)}
                  className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-surface-2"
                >
                  <Avatar
                    id={member.id}
                    name={member.displayName}
                    src={member.avatarUrl}
                    size="sm"
                  />
                  <span className="flex-1 truncate">
                    {member.id === me.id
                      ? t('{{name}} (yo)', { name: member.displayName })
                      : member.displayName}
                  </span>
                  {isSelected && <Check className="size-4 text-accent" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Popover>
  );
}

function DuePicker({
  value,
  done,
  onChange,
}: {
  value: string | null;
  done: boolean;
  onChange: (value: string | null) => void;
}) {
  const { t } = useTranslation();
  const state = value && !done ? dueState(value) : null;
  const quick = [
    { label: t('Hoy'), value: isoDateFromToday(0) },
    { label: t('Mañana'), value: isoDateFromToday(1) },
    { label: t('En 1 semana'), value: isoDateFromToday(7) },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1">
      <DatePicker
        aria-label={t('Fecha límite')}
        variant="ghost"
        size="sm"
        hideIcon
        clearable
        shortcuts={[]}
        value={value}
        onChange={onChange}
        formatValue={(date) => (
          <span
            className={cn(
              state === 'overdue' && 'text-danger',
              (state === 'today' || state === 'soon') && 'text-warning',
            )}
          >
            {formatDueDate(date)}
            {state === 'overdue' && ` · ${t('vencida')}`}
          </span>
        )}
        placeholder={t('Sin fecha')}
      />
      {quick.map((q) => (
        <button
          key={q.label}
          type="button"
          onClick={() => onChange(q.value)}
          className={cn(
            'h-6 rounded-md px-2 text-xs ring-1 ring-inset ring-border hover:bg-surface-2',
            value === q.value ? 'bg-accent-soft text-accent ring-accent/30' : 'text-muted',
          )}
        >
          {q.label}
        </button>
      ))}
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="h-6 rounded-md px-2 text-xs text-subtle hover:text-danger"
        >
          {t('Quitar')}
        </button>
      )}
    </div>
  );
}

/** Borderless auto-growing textarea that saves on blur (edit mode). */
function InlineText({
  value,
  onSave,
  multiline = false,
  ...props
}: {
  value: string;
  onSave: (value: string) => void;
  multiline?: boolean;
  className?: string;
  placeholder: string;
  label: string;
  maxLength: number;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <AutoTextarea
      value={draft}
      onChange={setDraft}
      onBlur={() => onSave(draft)}
      onEnter={multiline ? undefined : (el) => el.blur()}
      aria-label={props.label}
      placeholder={props.placeholder}
      maxLength={props.maxLength}
      className={props.className}
    />
  );
}

function AutoTextarea({
  value,
  onChange,
  onEnter,
  className,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
  onEnter?: ((el: HTMLTextAreaElement) => void) | undefined;
  onBlur?: () => void;
  className?: string | undefined;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  'aria-label': string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  // Grows with its text. Measured again whenever its width changes: when the drawer is still
  // opening (or hidden) the first measure can be 0, which left the title squashed.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (onEnter && e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onEnter(e.currentTarget);
        }
      }}
      className={cn(
        'block min-h-[1.75em] w-full resize-none rounded-lg bg-transparent px-2 py-1 -mx-2 placeholder:text-subtle hover:bg-surface-2/60 focus:bg-surface-2/60 focus:outline-none focus:ring-2 focus:ring-accent/40',
        className,
      )}
      {...props}
    />
  );
}
